import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Stack,
  Group,
  Button,
  Text,
  Table,
  Badge,
  ActionIcon,
  Menu,
  Breadcrumbs,
  Anchor,
  Loader,
  Alert,
  Image,
  Avatar,
  Box,
  SimpleGrid,
  TextInput,
  Select,
  Drawer,
  Divider,
} from '@mantine/core';
import {
  IconPlus,
  IconTrash,
  IconClipboardCheck,
  IconDots,
  IconUpload,
  IconAlertCircle,
  IconPhoto,
  IconSearch,
  IconFilter,
  IconX,
  IconEye,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { AddAssetsModal } from './components/AddAssetsModal';
import { AuditAssetMobileCard } from './components/AuditAssetMobileCard';

export function AuditDetailPage() {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();

  const [audit, setAudit] = useState<any>(null);
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // Extract unique locations and categories for filter dropdowns
  const locations = useMemo(() => {
    const unique = new Map<string, string>();
    assets.forEach(item => {
      if (item.asset?.location_id && item.asset?.location?.name) {
        unique.set(item.asset.location_id, item.asset.location.name);
      }
    });
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }));
  }, [assets]);

  const categories = useMemo(() => {
    const unique = new Map<string, string>();
    assets.forEach(item => {
      if (item.asset?.category_id && item.asset?.category?.name) {
        unique.set(item.asset.category_id, item.asset.category.name);
      }
    });
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }));
  }, [assets]);

  // Filtered assets
  const filteredAssets = useMemo(() => {
    return assets.filter(item => {
      const asset = item.asset;

      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          asset?.tag_id?.toLowerCase().includes(searchLower) ||
          asset?.description?.toLowerCase().includes(searchLower) ||
          asset?.serial_number?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (statusFilter && item.status !== statusFilter) return false;

      // Location filter
      if (locationFilter && asset?.location_id !== locationFilter) return false;

      // Category filter
      if (categoryFilter && asset?.category_id !== categoryFilter) return false;

      return true;
    });
  }, [assets, search, statusFilter, locationFilter, categoryFilter]);

  const activeFilterCount = [statusFilter, locationFilter, categoryFilter].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter(null);
    setLocationFilter(null);
    setCategoryFilter(null);
  };

  // Navigate to review page starting at a specific asset
  const goToReviewAsset = (assetAuditId: string) => {
    // Find the index in the full (unfiltered) assets array
    const index = assets.findIndex(a => a.id === assetAuditId);
    if (index >= 0) {
      navigate(`/audits/${auditId}/review?startAt=${index}`);
    } else {
      navigate(`/audits/${auditId}/review`);
    }
  };

  useEffect(() => {
    loadData();
  }, [auditId]);

  async function loadData() {
    if (!auditId) return;

    setLoading(true);
    setError(null);

    try {
      // Get auth token from localStorage
      const authKey = Object.keys(localStorage).find(key =>
        key.startsWith('sb-') && key.endsWith('-auth-token')
      );

      if (!authKey) {
        throw new Error('Not authenticated');
      }

      const authData = JSON.parse(localStorage.getItem(authKey) || '{}');
      const token = authData?.access_token;

      if (!token) {
        throw new Error('Not authenticated');
      }

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Fetch audit
      const auditRes = await fetch(
        `${baseUrl}/rest/v1/asset_audit_events?id=eq.${auditId}`,
        {
          headers: {
            'apikey': anonKey,
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const auditData = await auditRes.json();

      if (!auditData || auditData.length === 0) {
        throw new Error('Audit not found');
      }

      // Fetch assets with location and category
      const assetsRes = await fetch(
        `${baseUrl}/rest/v1/asset_audits?audit_event_id=eq.${auditId}&select=*,asset:assets(*,location:locations(*),category:categories(*),department:departments(*))&order=created_at.asc`,
        {
          headers: {
            'apikey': anonKey,
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const assetsData = await assetsRes.json();

      setAudit(auditData[0]);
      setAssets(assetsData || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load audit');
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function removeAsset(id: string) {
    if (!confirm('Remove this asset?')) return;

    try {
      const authKey = Object.keys(localStorage).find(key =>
        key.startsWith('sb-') && key.endsWith('-auth-token')
      );
      const authData = JSON.parse(localStorage.getItem(authKey!) || '{}');
      const token = authData?.access_token;

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      await fetch(`${baseUrl}/rest/v1/asset_audits?id=eq.${id}`, {
        method: 'DELETE',
        headers: {
          'apikey': anonKey,
          'Authorization': `Bearer ${token}`,
        },
      });

      setAssets(prev => prev.filter(a => a.id !== id));
      notifications.show({
        title: 'Removed',
        message: 'Asset removed from audit',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to remove asset',
        color: 'red',
      });
    }
  }

  if (loading) {
    return (
      <Container fluid py="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text>Loading audit...</Text>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error}
        </Alert>
        <Button mt="md" onClick={() => navigate('/audits')}>
          Back to Audits
        </Button>
      </Container>
    );
  }

  if (!audit) {
    return (
      <Container fluid py="xl">
        <Text>Audit not found</Text>
        <Button mt="md" onClick={() => navigate('/audits')}>
          Back to Audits
        </Button>
      </Container>
    );
  }

  const stats = {
    pending: assets.filter(a => a.status === 'pending').length,
    found: assets.filter(a => a.status === 'found').length,
    missing: assets.filter(a => a.status === 'missing').length,
    damaged: assets.filter(a => a.status === 'damaged').length,
  };

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/audits')}>Audits</Anchor>
          <Text>{audit.name}</Text>
        </Breadcrumbs>

        <Group justify="apart">
          <div>
            <Title order={1}>{audit.name}</Title>
            <Text size="sm" c="dimmed">
              {assets.length} assets
            </Text>
          </div>

          <Group>
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => setAddModalOpen(true)}
            >
              Add Assets
            </Button>
            <Button
              leftSection={<IconClipboardCheck size={16} />}
              onClick={() => navigate(`/audits/${auditId}/review`)}
              disabled={assets.length === 0}
            >
              Start Review
            </Button>
          </Group>
        </Group>

        <Paper p="md" withBorder>
          <SimpleGrid cols={{ base: 2, xs: 4 }} spacing="md">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Pending
              </Text>
              <Text size="xl" fw={700}>
                {stats.pending}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Found
              </Text>
              <Text size="xl" fw={700} c="green">
                {stats.found}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Missing
              </Text>
              <Text size="xl" fw={700} c="red">
                {stats.missing}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Damaged
              </Text>
              <Text size="xl" fw={700} c="orange">
                {stats.damaged}
              </Text>
            </div>
          </SimpleGrid>
        </Paper>

        {/* Search and filters */}
        {assets.length > 0 && (
          <Paper p="md" withBorder>
            <Group gap="sm" wrap="wrap">
              <TextInput
                placeholder="Search assets..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ flex: 1, minWidth: 200 }}
              />
              <Button
                variant="light"
                leftSection={<IconFilter size={16} />}
                onClick={() => setFilterDrawerOpen(true)}
                rightSection={
                  activeFilterCount > 0 ? (
                    <Badge size="sm" variant="filled" color="blue" circle>
                      {activeFilterCount}
                    </Badge>
                  ) : undefined
                }
              >
                Filters
              </Button>
            </Group>
            {activeFilterCount > 0 && (
              <Group gap="xs" mt="sm">
                <Text size="sm" c="dimmed">Active filters:</Text>
                {statusFilter && (
                  <Badge
                    variant="light"
                    rightSection={
                      <ActionIcon size="xs" variant="transparent" onClick={() => setStatusFilter(null)}>
                        <IconX size={12} />
                      </ActionIcon>
                    }
                  >
                    Status: {statusFilter}
                  </Badge>
                )}
                {locationFilter && (
                  <Badge
                    variant="light"
                    rightSection={
                      <ActionIcon size="xs" variant="transparent" onClick={() => setLocationFilter(null)}>
                        <IconX size={12} />
                      </ActionIcon>
                    }
                  >
                    Location: {locations.find(l => l.value === locationFilter)?.label}
                  </Badge>
                )}
                {categoryFilter && (
                  <Badge
                    variant="light"
                    rightSection={
                      <ActionIcon size="xs" variant="transparent" onClick={() => setCategoryFilter(null)}>
                        <IconX size={12} />
                      </ActionIcon>
                    }
                  >
                    Category: {categories.find(c => c.value === categoryFilter)?.label}
                  </Badge>
                )}
                <Button variant="subtle" size="xs" onClick={clearFilters}>
                  Clear all
                </Button>
              </Group>
            )}
          </Paper>
        )}

        {assets.length === 0 ? (
          <Paper p="md" withBorder>
            <Stack align="center" py="xl">
              <IconUpload size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />
              <Text c="dimmed">No assets yet. Click "Add Assets" to start.</Text>
            </Stack>
          </Paper>
        ) : filteredAssets.length === 0 ? (
          <Paper p="md" withBorder>
            <Stack align="center" py="xl">
              <Text c="dimmed">No assets match your filters.</Text>
              <Button variant="subtle" onClick={clearFilters}>Clear filters</Button>
            </Stack>
          </Paper>
        ) : (
          <>
            {/* Mobile card view */}
            <Box hiddenFrom="sm">
              <Stack gap={0}>
                {filteredAssets.map((item) => (
                  <AuditAssetMobileCard
                    key={item.id}
                    item={item}
                    onRemove={removeAsset}
                    onClick={() => goToReviewAsset(item.id)}
                  />
                ))}
              </Stack>
            </Box>

            {/* Desktop table view */}
            <Box visibleFrom="sm">
              <Paper p="md" withBorder>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Photo</Table.Th>
                      <Table.Th>Asset Tag</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Location</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Notes</Table.Th>
                      <Table.Th>Completed</Table.Th>
                      <Table.Th></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredAssets.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => goToReviewAsset(item.id)}
                      >
                        <Table.Td>
                          {item.asset?.photo_url ? (
                            <Image
                              src={item.asset.photo_url}
                              alt={item.asset.description}
                              w={40}
                              h={40}
                              radius="sm"
                              fit="cover"
                            />
                          ) : (
                            <Avatar color="gray" size={40} radius="sm">
                              <IconPhoto size={20} />
                            </Avatar>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text fw={500}>{item.asset?.tag_id || '-'}</Text>
                        </Table.Td>
                        <Table.Td>{item.asset?.description || '-'}</Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {item.asset?.location?.name || '-'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={
                              item.status === 'found'
                                ? 'green'
                                : item.status === 'missing'
                                ? 'red'
                                : item.status === 'damaged'
                                ? 'orange'
                                : 'gray'
                            }
                          >
                            {item.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" lineClamp={1}>
                            {item.notes || '-'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {item.completed_at
                            ? new Date(item.completed_at).toLocaleDateString()
                            : '-'}
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToReviewAsset(item.id);
                              }}
                              title="Review asset"
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                            <Menu position="bottom-end">
                              <Menu.Target>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconDots size={16} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  leftSection={<IconTrash size={16} />}
                                  color="red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeAsset(item.id);
                                  }}
                                >
                                  Remove
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Box>

            <Text size="sm" c="dimmed">
              Showing {filteredAssets.length} of {assets.length} assets
            </Text>
          </>
        )}

        {/* Filter Drawer */}
        <Drawer
          opened={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          title="Filters"
          position="right"
          size="sm"
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              clearable
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: 'pending', label: 'Pending' },
                { value: 'found', label: 'Found' },
                { value: 'missing', label: 'Missing' },
                { value: 'damaged', label: 'Damaged' },
              ]}
            />

            <Select
              label="Location"
              placeholder="All locations"
              clearable
              value={locationFilter}
              onChange={setLocationFilter}
              data={locations}
              disabled={locations.length === 0}
            />

            <Select
              label="Category"
              placeholder="All categories"
              clearable
              value={categoryFilter}
              onChange={setCategoryFilter}
              data={categories}
              disabled={categories.length === 0}
            />

            <Divider />

            <Group justify="space-between">
              <Button variant="subtle" onClick={clearFilters}>
                Clear all
              </Button>
              <Button onClick={() => setFilterDrawerOpen(false)}>
                Apply
              </Button>
            </Group>
          </Stack>
        </Drawer>

        <AddAssetsModal
          opened={addModalOpen}
          onClose={() => {
            setAddModalOpen(false);
            loadData();
          }}
          auditEventId={auditId!}
          organizationId={audit.organization_id}
        />
      </Stack>
    </Container>
  );
}
